/**
 * @file     useListingsAccess.ts
 * @purpose  Client-side read of the listings-access gate state. Intentionally
 *           DECOUPLED from auth/account state. Access is granted by the
 *           `relta_listings_unlocked` cookie (lead captured / session mints it)
 *           OR by the free-preview allowance. Once a visitor has opened
 *           FREE_PREVIEW_LIMIT distinct listings, the next new one locks behind
 *           VOW registration. Both counters live in browser storage, so a
 *           cleared browser or a fresh device starts over; that is the
 *           intended posture, not a leak to plug.
 *           Re-checks on the `relta:listings-unlocked` event and on tab focus /
 *           bfcache restore.
 */
'use client';

import { useEffect, useState } from 'react';
import { LISTINGS_UNLOCKED_COOKIE } from '@/services/auth/listingsAccess';
import { getAnonymousId, getSessionId } from '@/services/analytics/anonId';
import { readViewedListingKeys } from '@/hooks/useViewedListings';
import { flushListingSignals } from '@/hooks/useListingSignals';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { reltaTrack } from '@/hooks/useReltaTrack';
import { isCrawlerUserAgent } from '@/services/anti-scraping/botDetection';

/** Window event fired by any flow that mints the access cookie. */
export const LISTINGS_UNLOCKED_EVENT = 'relta:listings-unlocked';

/** Distinct listing details an anonymous visitor may view before the gate can
 *  appear. Tracked in localStorage, so it is per-browser and resettable by
 *  design — this is a conversion prompt, not a paywall. */
export const FREE_PREVIEW_LIMIT = 4;

/** Visits a visitor must have made before the gate can appear at all. One
 *  means the allowance is enforced from the first visit. */
export const MIN_VISITS_BEFORE_GATE = 1;

/** Idle span that ends a visit. Matches the 30-minute analytics session window
 *  so "visit" means the same thing here as it does in reporting. */
const VISIT_IDLE_MS = 30 * 60 * 1000;

const VIEWED_KEY = 'relta.gate.viewed';
const VISITS_KEY = 'relta.gate.visits';
const VISIT_ACTIVE_KEY = 'relta.gate.visit.active';

/** Crawlers (search and AI, including JS renderers that keep storage between
 *  pages) always see the full listing: the server HTML is complete, and a
 *  wall would only blur it for an indexer and spend the allowance counters. */
function isCrawler(): boolean {
  return typeof navigator !== 'undefined' && isCrawlerUserAgent(navigator.userAgent);
}

function readUnlockedCookie(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.split('; ').some((c) => c.startsWith(`${LISTINGS_UNLOCKED_COOKIE}=`));
}

function readViewed(): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(VIEWED_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

/** Every distinct listing this browser has opened, across both counters: the
 *  gate's own store (map sheet + listing modals) and the viewed-listings store
 *  the slug page writes. Ray's rule counts properties seen, not the surface
 *  they were seen on. */
function seenListingKeys(): Set<string> {
  const seen = new Set(readViewed());
  for (const key of readViewedListingKeys()) seen.add(key);
  return seen;
}

function recordViewed(key: string): void {
  try {
    const set = new Set(readViewed());
    set.add(key);
    localStorage.setItem(VIEWED_KEY, JSON.stringify([...set]));
  } catch {
    /* quota / disabled storage — the free view just won't persist */
  }
}

/**
 * Count the visit this evaluation belongs to, and return its number.
 *
 * A visit continues while the tab-scoped marker survives and activity stays
 * inside the idle window; otherwise this is a new visit. Idempotent within a
 * visit, so the re-syncs on focus / bfcache do not inflate the count. Storage
 * being unavailable reads as visit 1 forever, which leaves the visitor ungated.
 */
function noteVisit(): number {
  let count = 0;
  let lastSeenMs = 0;
  try {
    const raw = localStorage.getItem(VISITS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { count?: number; lastSeenMs?: number };
      count = Number(parsed.count) || 0;
      lastSeenMs = Number(parsed.lastSeenMs) || 0;
    }
  } catch {
    return 1;
  }

  // Without the tab marker a reload is indistinguishable from a return, so fall
  // back to the idle window alone rather than counting a visit per evaluation.
  let active = true;
  try {
    active = sessionStorage.getItem(VISIT_ACTIVE_KEY) === '1';
  } catch {
    /* session storage disabled — idle window is the only continuity signal */
  }

  const now = Date.now();
  const continuing = active && count > 0 && now - lastSeenMs <= VISIT_IDLE_MS;
  const visit = continuing ? count : count + 1;
  try {
    localStorage.setItem(VISITS_KEY, JSON.stringify({ count: visit, lastSeenMs: now }));
    sessionStorage.setItem(VISIT_ACTIVE_KEY, '1');
  } catch {
    /* quota / disabled storage — the visit just won't persist */
  }
  return visit;
}

/**
 * The floor every anonymous sign-up wall driven by property viewership shares:
 * a wall may only appear once the visitor has made MIN_VISITS_BEFORE_GATE
 * visits AND has already seen the full allowance of distinct properties. Below
 * the floor,
 * no wall shows at all.
 *
 * Counts the visit as a side effect, so any surface that consults the floor
 * keeps the visit counter current.
 */
export function isGateEligible(): boolean {
  if (isCrawler()) return false;
  const visit = noteVisit();
  return visit >= MIN_VISITS_BEFORE_GATE && seenListingKeys().size >= FREE_PREVIEW_LIMIT;
}

/** Notify any mounted gate that access was just granted (instant unlock). */
export function announceListingsUnlocked(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(LISTINGS_UNLOCKED_EVENT));
}

// ─── Phase-1 dark-launch: log intent score vs current gate decision ──────────
// Fire-and-forget. Computes the visitor's behavioral intent score at the moment
// the gate evaluates and logs it next to the (unchanged) flat-limit decision so
// the Phase-2 threshold can be calibrated from real traffic. Changes NOTHING
// about gate behavior. Deduped per listing per session to avoid re-firing on
// the visibilitychange / pageshow re-syncs.
const snapshotLogged = new Set<string>();

// Most recent intent snapshot this session. Carried so the conversion event can
// embed the score the visitor had at the gate — that makes the OUTCOME
// self-describing, so the Phase-1 calibration is a single-table groupby
// (conversion rate per score bucket) instead of a fragile cross-event /
// cross-identity join. Without an outcome variable the dark-launch can only
// describe the score distribution, never validate a threshold against it.
let lastSnapshot:
  | { score: number; tier: string; locked: boolean; viewedCount: number; listingKey: string; atMs: number }
  | null = null;
let conversionLogged = false;

function logIntentSnapshot(listingKey: string, locked: boolean, viewedCount: number): void {
  if (typeof window === 'undefined' || snapshotLogged.has(listingKey)) return;
  snapshotLogged.add(listingKey);
  const anonymousId = getAnonymousId();
  const sessionId = getSessionId();
  // Commit any buffered behavioral signals (list/map/detail) BEFORE scoring, so
  // the score reflects this session's activity instead of a stale read.
  void flushListingSignals()
    .then(() =>
      secureFetch('/api/intent/score', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ anonymousId, sessionId }),
      }),
    )
    .then((r) => (r.ok ? r.json() : null))
    .then((data: { score?: number; tier?: string; distinctListings?: number } | null) => {
      if (!data) return;
      const score = data.score ?? 0;
      const tier = data.tier ?? 'cold';
      reltaTrack('auth_gate.intent_snapshot', {
        score,
        tier,
        distinct_listings: data.distinctListings,
        locked,
        viewed_count: viewedCount,
        listing_key: listingKey,
      });
      lastSnapshot = { score, tier, locked, viewedCount, listingKey, atMs: Date.now() };
    })
    .catch(() => { snapshotLogged.delete(listingKey); /* allow a later retry */ });
}

// Conversion outcome: the visitor was scored, then access was granted (signup /
// lead-session mint). Fires once per session, embedding the gate-time score so
// score→converted can be measured directly. Visitors who never convert simply
// never emit this — the snapshot rows are the denominator, conversions the
// numerator, joined inline by the carried score.
function logGateConversion(): void {
  if (typeof window === 'undefined' || conversionLogged || !lastSnapshot) return;
  conversionLogged = true;
  const s = lastSnapshot;
  reltaTrack('auth_gate.conversion', {
    score: s.score,
    tier: s.tier,
    was_locked: s.locked,
    viewed_count: s.viewedCount,
    listing_key: s.listingKey,
    seconds_since_snapshot: Math.max(0, Math.round((Date.now() - s.atMs) / 1000)),
  });
}

/**
 * @param listingKey  The listing currently being shown. Drives the free-preview
 *   allowance. When omitted (no specific listing in context) the surface cannot
 *   spend the allowance, so it locks once the visit and view thresholds are met.
 */
export function useListingsAccess(listingKey?: string): { locked: boolean; resolved: boolean } {
  // `resolved` guards a first-paint flash: on the server and the first client
  // render we don't know yet, so callers must treat !resolved as "don't gate".
  const [state, setState] = useState<{ locked: boolean; resolved: boolean }>({
    locked: false,
    resolved: false,
  });

  useEffect(() => {
    const sync = () => {
      if (isCrawler()) {
        setState({ locked: false, resolved: true });
        return;
      }
      // Unlocked via lead/session cookie → never gate. If this visitor was
      // scored earlier this session, the unlock is a gate conversion — log it.
      if (readUnlockedCookie()) {
        logGateConversion();
        setState({ locked: false, resolved: true });
        return;
      }
      const visit = noteVisit();
      const seen = seenListingKeys();
      const alreadySeen = listingKey ? seen.has(listingKey) : false;

      // Record before deciding, so the listing that takes the visitor to the
      // allowance is itself viewable and the NEXT new one is what locks. A
      // property they have already opened always stays open.
      const spending = Boolean(listingKey) && !alreadySeen && seen.size < FREE_PREVIEW_LIMIT;
      if (spending && listingKey) {
        recordViewed(listingKey);
        seen.add(listingKey);
      }

      // Both conditions must hold: enough visits AND a spent allowance.
      const locked =
        !alreadySeen &&
        !spending &&
        visit >= MIN_VISITS_BEFORE_GATE &&
        seen.size >= FREE_PREVIEW_LIMIT;

      if (listingKey) logIntentSnapshot(listingKey, locked, seen.size);
      setState({ locked, resolved: true });
    };
    sync();
    window.addEventListener(LISTINGS_UNLOCKED_EVENT, sync);
    document.addEventListener('visibilitychange', sync);
    // pageshow fires on bfcache restore (browser Back after a failed OAuth
    // bounce), where effects don't re-run — re-evaluate so the gate reappears
    // without a manual refresh.
    window.addEventListener('pageshow', sync);
    return () => {
      window.removeEventListener(LISTINGS_UNLOCKED_EVENT, sync);
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('pageshow', sync);
    };
  }, [listingKey]);

  return state;
}
